import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';

import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { PrimaryButton, SecondaryButton } from '../../_core/_ui/buttons.component';
import { ParentVerifyContactContainer } from '../../User/ParentVerifyContactForm/parent-verify-contact.container';
import { buildSignOut, parentUser } from '../../../lib/services/auth.service';
import { SessionType } from '../../../lib/types/session.types';
import { formatPhone } from '../../../lib/helpers/phone.helpers';
import { parentLoginPathWithNext } from '../../../lib/helpers/parent-links.helpers';
import { mutedTextColor, warnColor, warnHighlightColor, warnTextColor } from '../../../lib/theme';
import { PLAYER_PAGE_CARD_MAX_WIDTH } from './public-edit-player.constants';
import { RsvpContactIdentity } from './rsvp-contact-identity.component';
import { messages } from './rsvp-identity-banner.messages';

interface IRsvpIdentityBannerProps {
  credentialContact?: IRsvpCredentialContact;
}

// Names the contact a link-authenticated RSVP is recorded as, and warns when a
// DIFFERENT parent is signed in — the URL token wins, so the response is theirs.
export const RsvpIdentityBanner: React.FC<IRsvpIdentityBannerProps> = ({ credentialContact }) => {
  const history = useHistory();
  const location = useLocation();
  const parent = useReactiveVar(parentUser);
  const signOut = buildSignOut();
  // The credential being verified — doubles as the dialog's open flag.
  const [verifyTarget, setVerifyTarget] = useState<string | null>(null);
  const closeVerify = useCallback(() => setVerifyTarget(null), []);

  // Sign out first so the login form starts clean, then return here (token and
  // all) once the right person has verified their own code.
  const handleSwitchAccount = useCallback(async () => {
    try {
      await signOut(SessionType.PARENT);
    } catch (err) {
      console.error('parent sign-out before account switch failed:', err);
    }
    history.push(parentLoginPathWithNext(location.pathname, location.search));
  }, [signOut, history, location]);

  // The server decides what is offerable — it excludes credentials this parent
  // already holds and ones another parent holds (which the unique index refuses).
  const pendingCredentials = useMemo(() => {
    if (!credentialContact) return [];
    const { email, phone, canAddEmail, canAddPhone } = credentialContact;
    const pending: { value: string; label: string }[] = [];
    if (email && canAddEmail) pending.push({ value: email, label: email });
    if (phone && canAddPhone) pending.push({ value: phone, label: formatPhone(phone) });
    return pending;
  }, [credentialContact]);

  // Absent on the parent-JWT path — the page header already names that parent.
  if (!credentialContact) return null;

  const contactLabel = credentialContact.name
    || credentialContact.email
    || formatPhone(credentialContact.phone);
  if (!contactLabel) return null;

  const contactIdentity = (
    <RsvpContactIdentity
      name={credentialContact.name}
      email={credentialContact.email}
      phone={credentialContact.phone}
    />
  );

  const verifyDialog = (
    <Dialog
      open={!!verifyTarget}
      onClose={closeVerify}
      maxWidth="sm"
      fullWidth
      aria-label="Add this contact to my account"
    >
      <DialogContent>
        <ParentVerifyContactContainer
          key={verifyTarget}
          initialIdentifier={verifyTarget || ''}
          onSuccess={closeVerify}
        />
      </DialogContent>
      <DialogActions>
        <SecondaryButton variant="text" onClick={closeVerify}>
          <IntlMsg {...messages.cancel} />
        </SecondaryButton>
      </DialogActions>
    </Dialog>
  );

  const addButtons = pendingCredentials.map((credential) => (
    <PrimaryButton
      key={credential.value}
      variant="contained"
      size="small"
      onClick={() => setVerifyTarget(credential.value)}
    >
      <IntlMsg {...messages.addToAccount} values={{ credential: credential.label }} />
    </PrimaryButton>
  ));

  // Once this parent has proved one of the contact's credentials there is
  // nothing to tell them — only an unclaimed address still needs an action.
  if (parent && credentialContact.isVerifiedBySignedInParent) {
    return (
      <>
        {pendingCredentials.length > 0 && (
          <Box
            display="flex"
            flexWrap="wrap"
            style={{ maxWidth: PLAYER_PAGE_CARD_MAX_WIDTH, margin: '0 auto 12px', gap: 8 }}
          >
            {addButtons}
          </Box>
        )}
        {verifyDialog}
      </>
    );
  }

  const body = parent ? (
    <Box
      style={{
        maxWidth: PLAYER_PAGE_CARD_MAX_WIDTH,
        margin: '0 auto 12px',
        background: warnHighlightColor,
        border: `1px solid ${warnColor}`,
        borderRadius: 12,
        padding: '12px 16px',
      }}
    >
      <Typography variant="body2" style={{ color: warnTextColor }}>
        <IntlMsg {...messages.mismatchWarning} />
      </Typography>
      <Box mt={1}>
        <Typography variant="body2" style={{ color: warnTextColor }}>
          <IntlMsg
            {...messages.signedInAs}
            values={{
              identity: (
                <RsvpContactIdentity name={parent.name} email={parent.email} phone={parent.phone} />
              ),
            }}
          />
        </Typography>
        <Typography variant="body2" style={{ color: warnTextColor }}>
          <IntlMsg {...messages.linkSentTo} values={{ identity: contactIdentity }} />
        </Typography>
      </Box>
      <Box mt={1.5} display="flex" flexWrap="wrap" style={{ gap: 8 }}>
        {addButtons}
        <SecondaryButton variant="outlined" size="small" onClick={handleSwitchAccount}>
          <IntlMsg {...messages.switchAccount} values={{ contact: contactLabel }} />
        </SecondaryButton>
      </Box>
    </Box>
  ) : (
    <Box style={{ maxWidth: PLAYER_PAGE_CARD_MAX_WIDTH, margin: '0 auto 12px' }}>
      <Typography variant="caption" style={{ color: mutedTextColor }}>
        <IntlMsg {...messages.respondingAs} values={{ identity: contactIdentity }} />
      </Typography>
    </Box>
  );

  return (
    <>
      {body}
      {verifyDialog}
    </>
  );
};
